import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from 'helpers/Analytics';
import { useParams } from 'react-router';
import { Link, Prompt } from 'react-router-dom';
import { Col, Grid, HelpBlock, Row } from 'react-bootstrap';
import Panel from 'components/Panel/Panel';
import styles from './ProjectForm.module.scss';
import {
  updateProject,
  loadCredits,
  loadProject,
  updateCredits,
  deleteProject,
} from 'redux/modules/textProjects';
import useAuth from 'hooks/useAuth';
import Loader from 'components/Loader/Loader';
import ProjectMediaPanel from 'components/TextProjects/ProjectMediaPanel';
import ProjectDescriptionField from 'components/TextProjects/ProjectDescriptionField';
import ProjectRolesField from 'components/TextProjects/ProjectRolesField';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import AlertBox from 'components/AlertBox/AlertBox';
import { setSuccessMessage } from 'redux/modules/successMessage';
import {
  setProjectPublishFlash,
  toStorablePreviewUrl,
} from 'helpers/projectPublishFlash';

const roleValues = (roles) =>
  (roles || [])
    .map((role) => role?.value || role)
    .filter(Boolean)
    .slice()
    .sort();

const rolesEqual = (a, b) => {
  const left = roleValues(a);
  const right = roleValues(b);
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const EditProject = () => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { hash } = useParams();

  useEffect(() => {
    dispatch(loadProject('c' + hash));
    dispatch(loadCredits('c' + hash));
  }, []);

  const projectSelect = (state) => state.textProjects;
  const projectState = useSelector(projectSelect);

  const {
    loadingProject,
    loadingCredits,
    updatingProject,
    updatingCredits,
    project,
    credits,
  } = projectState;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [initialTitle, setInitialTitle] = useState('');
  const [initialDescription, setInitialDescription] = useState('');
  const [initialRoles, setInitialRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [allowLeave, setAllowLeave] = useState(false);
  const [mediaPayload, setMediaPayload] = useState({
    file: null,
    thumbnail: null,
    crop: null,
    remove_media: false,
    remove_thumbnail: false,
    uploading: false,
    previewUrl: null,
  });

  useEffect(() => {
    if (!loadingProject && !loadingCredits) {
      setLoading(false);
      const nextTitle = project?.title || '';
      const nextDescription = project?.description || '';
      const creditedRoles = credits?.[0]?.roles?.map((role) => role.name) || [];
      setTitle(nextTitle);
      setDescription(nextDescription);
      setSelectedRoles(creditedRoles);
      setInitialTitle(nextTitle);
      setInitialDescription(nextDescription);
      setInitialRoles(creditedRoles);
    } else {
      setLoading(true);
    }
  }, [project, credits, loadingProject, loadingCredits]);

  const handleRolesChange = (roles) => {
    setSelectedRoles(roles);
  };

  const isProcessing = project?.state === 'processing';
  const busy =
    updatingProject || updatingCredits || mediaPayload.uploading || deleting;

  const mediaDirty = Boolean(
    mediaPayload.file ||
      mediaPayload.thumbnail ||
      mediaPayload.remove_media ||
      mediaPayload.remove_thumbnail
  );

  const hasChanges = useMemo(() => {
    if (loading) return false;
    if ((title || '') !== (initialTitle || '')) return true;
    if ((description || '') !== (initialDescription || '')) return true;
    if (!rolesEqual(selectedRoles, initialRoles)) return true;
    if (mediaDirty) return true;
    return false;
  }, [
    loading,
    title,
    initialTitle,
    description,
    initialDescription,
    selectedRoles,
    initialRoles,
    mediaDirty,
  ]);

  useEffect(() => {
    if (!__CLIENT__) return undefined;

    // After a successful save/delete we navigate away — do not re-arm the
    // browser leave dialog once allowLeave is set.
    if (hasChanges && !busy && !allowLeave) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [hasChanges, busy, allowLeave]);

  const buildUpdateData = () => {
    const data = {
      title,
      description,
    };

    if (mediaPayload.remove_media) {
      data.remove_media = true;
    } else if (mediaPayload.file) {
      data.file = mediaPayload.file;
    }

    if (mediaPayload.remove_thumbnail) {
      data.remove_thumbnail = true;
    } else if (mediaPayload.thumbnail) {
      data.thumbnail = mediaPayload.thumbnail;
      if (mediaPayload.crop) {
        data.crop = mediaPayload.crop;
      }
    }

    return data;
  };

  const clearLeaveGuards = () => {
    setAllowLeave(true);
    if (__CLIENT__) {
      window.onbeforeunload = null;
    }
  };

  const handleUpdateProject = () => {
    if (busy || !hasChanges) {
      return;
    }

    setErrors({});
    dispatch(updateProject(project.id, buildUpdateData()))
      .then((updated) => {
        const goToProject = async (creditsFailed = false) => {
          trackEvent('freelancer-updates-project');
          clearLeaveGuards();

          // Stash client preview for the project hero while reprocessing —
          // no "published" banner on edit.
          if (mediaPayload.file && project?.id) {
            const previewUrl = await toStorablePreviewUrl(
              mediaPayload.previewUrl
            );
            setProjectPublishFlash(project.id, {
              previewUrl,
              showBanner: false,
            });
          }

          dispatch(
            setSuccessMessage(
              creditsFailed
                ? 'Project updated. Roles could not be saved — try again from this page.'
                : 'Project successfully updated'
            )
          );
          window.location.href =
            (updated && updated.links && updated.links.main_relative) ||
            project.links.main_relative;
        };

        return dispatch(
          updateCredits(
            project.id,
            user.id,
            selectedRoles.map((role) => role?.value || role)
          )
        )
          .then(() => goToProject(false))
          .catch((error) => {
            console.error('Error updating credits:', error);
            goToProject(true);
          });
      })
      .catch((error) => {
        console.error('Error updating project:', error);
        setErrors(error.errors || {});
      });
  };

  const handleDeleteConfirm = () => {
    if (!project?.id || deleting) return;

    setDeleting(true);
    dispatch(deleteProject(project.id))
      .then(() => {
        clearLeaveGuards();
        setShowDeleteModal(false);
        window.location.href = '/projects/manage';
      })
      .catch((error) => {
        console.error('Error deleting project:', error);
        setDeleting(false);
        setShowDeleteModal(false);
      });
  };

  const initialRoleNames = initialRoles;

  return (
    <div className={styles.page}>
      <Prompt
        when={hasChanges && !allowLeave}
        message="Are you sure you want to leave your unsaved changes?"
      />
      <AlertBox
        show={showDeleteModal}
        title="Delete project"
        text="Are you sure you want to delete this project?"
        confirmButtonText="Yes I'm sure"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (!deleting) setShowDeleteModal(false);
        }}
        theme="danger"
      />
      <Grid>
        <Row>
          <Col xs={12}>
            <Breadcrumbs className={styles.breadcrumbs}>
              <Link to="/projects/manage">Manage projects</Link>
              Edit project
            </Breadcrumbs>
            <Panel
              className={`${styles.container} box-shadow`}
              showSeparators={false}
            >
              {loading ? (
                <Loader loading />
              ) : (
                <>
                  <div className={styles.section}>
                    <label className={styles.label} htmlFor="project-title">
                      Project title
                    </label>
                    <input
                      id="project-title"
                      type="text"
                      className={styles.input}
                      value={title || ''}
                      placeholder="e.g. Brand film for a fintech launch"
                      onChange={(event) => setTitle(event.target.value)}
                      maxLength={140}
                      disabled={busy}
                    />
                    <p className={styles.helper}>
                      Clients see this in search and on your portfolio.
                    </p>
                    {errors?.title && (
                      <HelpBlock className={styles.error}>
                        {errors?.title[0]}
                      </HelpBlock>
                    )}
                  </div>

                  <hr className={styles.divider} />

                  <div className={styles.section}>
                    <ProjectMediaPanel
                      mode="edit"
                      project={project}
                      disabled={busy}
                      processing={isProcessing}
                      projectTitle={title || project?.title || ''}
                      projectDescription={
                        description || project?.description || ''
                      }
                      firstRole={
                        (selectedRoles[0] &&
                          (selectedRoles[0].label ||
                            selectedRoles[0].value ||
                            selectedRoles[0])) ||
                        ''
                      }
                      onChange={setMediaPayload}
                    />
                    {errors?.file && (
                      <HelpBlock className={styles.error}>
                        {errors.file[0]}
                      </HelpBlock>
                    )}
                  </div>

                  <div className={styles.section}>
                    <ProjectDescriptionField
                      value={description || ''}
                      onChange={setDescription}
                      disabled={busy}
                      error={errors?.description?.[0]}
                    />
                  </div>

                  <div className={styles.section}>
                    <ProjectRolesField
                      key={JSON.stringify(initialRoleNames)}
                      maxValues={5}
                      existing={initialRoleNames}
                      onSelect={handleRolesChange}
                      disabled={busy}
                      error={errors?.roles?.[0]}
                    />
                  </div>

                  <div
                    className={`${styles.buttonContainer} ${styles.buttonContainerSplit}`}
                  >
                    <div className={styles.footerLeft}>
                      <button
                        type="button"
                        className={styles.deleteLink}
                        onClick={() => setShowDeleteModal(true)}
                        disabled={busy}
                      >
                        Delete project
                      </button>
                    </div>
                    <div className={styles.footerRight}>
                      <Link
                        to={project?.links?.main_relative || '/projects/manage'}
                        className={`${styles.cancelButton}${
                          busy ? ` ${styles.cancelDisabled}` : ''
                        }`}
                        onClick={(event) => {
                          if (busy) {
                            event.preventDefault();
                            return;
                          }
                          if (hasChanges) {
                            // Let react-router Prompt handle confirmation.
                            return;
                          }
                          clearLeaveGuards();
                        }}
                      >
                        Cancel
                      </Link>
                      <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={handleUpdateProject}
                        disabled={busy || !hasChanges}
                      >
                        {mediaPayload.uploading
                          ? 'Uploading media…'
                          : busy
                          ? 'Saving…'
                          : 'Save changes'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </Panel>
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

export default EditProject;
